import type { StorageApi } from '@backstage/frontend-plugin-api';

/** What the portal remembers of a user's onboarding (storageApi, bucket `onboarding`). */
export type OnboardingState = {
  /** The welcome tour was finished or skipped: it no longer starts on its own. */
  tourDone?: boolean;
  /** The checklist was hidden by the user. */
  checklistHidden?: boolean;
  /** Checklist items completed by an event or a click. */
  done?: string[];
  /** Page guides already shown (guides.tsx): they no longer start on their own. */
  guidesDone?: string[];
};

export const STORAGE_BUCKET = 'onboarding';
export const STORAGE_KEY = 'state';

/** What the portal knows about the user, besides the stored state. */
export interface OnboardingFacts {
  roles: string[];
  githubLinked: boolean;
  hasStarred: boolean;
}

export interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  href: string;
  /** Shown only to users with this Entra role. */
  role?: string;
  /** Completed by the user clicking it (no event tells us it was done). */
  completeOnClick?: boolean;
  /** Completed by what the portal already knows. */
  isDone?: (facts: OnboardingFacts) => boolean;
}

export const CHECKLIST: ChecklistItem[] = [
  {
    id: 'github',
    title: 'Lier ton compte GitHub',
    description: 'Pour voir tes PR et tes envs. 30 secondes.',
    href: '/ephemeral-envs',
    isDone: facts => facts.githubLinked,
  },
  {
    id: 'first-env',
    title: 'Lancer ton premier env',
    description: 'Déploie une branche depuis Ephemeral Envs.',
    href: '/ephemeral-envs',
    role: 'backstage-developer',
  },
  {
    id: 'star',
    title: 'Mettre un composant en favori',
    description:
      "L'étoile en haut d'une page du catalogue. Il apparaît ensuite sur ton accueil.",
    href: '/catalog',
    isDone: facts => facts.hasStarred,
  },
  {
    id: 'ticket',
    title: 'Chercher où en est un ticket',
    description: 'Delivery → Versions déployées, avec une clé Jira.',
    href: '/delivery/releases',
    completeOnClick: true,
  },
  {
    id: 'architecture',
    title: 'Lire Architecture & Conception',
    description: "La vue d'ensemble, avant de toucher au code.",
    href: '/docs/default/component/architecture-conception',
    completeOnClick: true,
  },
  {
    id: 'mcp',
    title: 'Connecter Backstage à ton IDE',
    description: 'Via MCP : Claude Code lance tes envs depuis le terminal.',
    href: '/docs/default/component/bobbee-backstage/mcp/',
    completeOnClick: true,
  },
];

/** Analytics actions (useAnalytics().captureEvent) that complete a checklist item. */
export const EVENT_ITEMS: Record<string, string> = {
  'link-github': 'github',
  'trigger-pipeline': 'first-env',
};

export function visibleItems(facts: OnboardingFacts): ChecklistItem[] {
  return CHECKLIST.filter(
    item => !item.role || facts.roles.includes(item.role),
  );
}

export function isItemDone(
  item: ChecklistItem,
  state: OnboardingState,
  facts: OnboardingFacts,
): boolean {
  return Boolean(state.done?.includes(item.id) || item.isDone?.(facts));
}

/** Stores an item as done; reads the latest state so concurrent updates are not lost. */
export async function markItemDone(storageApi: StorageApi, id: string) {
  const bucket = storageApi.forBucket(STORAGE_BUCKET);
  const state = bucket.snapshot<OnboardingState>(STORAGE_KEY).value ?? {};
  if (state.done?.includes(id)) return;
  await bucket.set<OnboardingState>(STORAGE_KEY, {
    ...state,
    done: [...(state.done ?? []), id],
  });
}

/** Stores a page guide as seen. */
export async function markGuideDone(storageApi: StorageApi, id: string) {
  const bucket = storageApi.forBucket(STORAGE_BUCKET);
  const state = bucket.snapshot<OnboardingState>(STORAGE_KEY).value ?? {};
  if (state.guidesDone?.includes(id)) return;
  await bucket.set<OnboardingState>(STORAGE_KEY, {
    ...state,
    guidesDone: [...(state.guidesDone ?? []), id],
  });
}

export async function updateState(
  storageApi: StorageApi,
  patch: Partial<OnboardingState>,
) {
  const bucket = storageApi.forBucket(STORAGE_BUCKET);
  const state = bucket.snapshot<OnboardingState>(STORAGE_KEY).value ?? {};
  await bucket.set<OnboardingState>(STORAGE_KEY, { ...state, ...patch });
}
